import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PortfolioFilter } from '../components/PortfolioFilter';
import ReviewAndCompare from './pnl/ReviewAndCompare';
import { exportStatementXlsx, exportStatementPdf } from './pnl/pnlExporters';
import { toast } from 'sonner';
import { Lock, Unlock, Settings, TrendingUp, TrendingDown, DollarSign, CheckCircle2, FileCheck, AlertTriangle, Download, Plus, X, ChevronDown, ChevronUp, Upload, CalendarIcon, FileText, RotateCcw, Pencil, Trash2, ToggleLeft, ToggleRight, FileSpreadsheet, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format, subDays } from 'date-fns';
import {
  isOperationalCostKey as isOperationalCostKeyV2,
  isSkipKey as isSkipKeyV2,
} from '../lib/pnlRegistry';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DEFAULT_FIXED = [
  { key: 'salary', label: 'Salary' },
  { key: 'rent', label: 'Rent' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'utilities', label: 'Utilities' },
  { key: 'other_fixed', label: 'Other Fixed' }
];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmt = v => v != null && !isNaN(v) ? `₹${Math.round(Number(v)).toLocaleString('en-IN')}` : '—';
const pctOf = (val, total) => total > 0 ? (val / total * 100).toFixed(1) : '0.0';
const varPct = (a, b) => b && b !== 0 ? ((a - b) / Math.abs(b) * 100).toFixed(1) : null;

function StatusTag({ finalized }) {
  return finalized ? (
    <Badge className="bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20 text-[10px] gap-0.5 h-5 px-1.5" data-testid="finalized-tag">
      <CheckCircle2 className="w-2.5 h-2.5" /> Finalized
    </Badge>
  ) : (
    <Badge variant="outline" className="text-[#D4A017] border-[#D4A017]/30 bg-[#D4A017]/5 text-[10px] gap-0.5 h-5 px-1.5" data-testid="live-tag">
      <AlertTriangle className="w-2.5 h-2.5" /> Live
    </Badge>
  );
}

const costColorPnL = (pct) => { if (pct == null) return ''; if (pct < 25) return 'text-[#2D6A4F]'; if (pct < 30) return 'text-[#D4A017]'; return 'text-[#C45A41]'; };

/* ── Unified P&L line row with budget + cost% columns ─────────────────────
   Excel-style freeze panes: the outer statement is a single `overflow: auto`
   scroll container. The header row is `sticky top:0` and the first cell of
   every row is `sticky left:0`; the top-left corner gets the highest z-index
   so both axes stay anchored during simultaneous scroll. First-column cells
   get a solid matching background so scrolled rows don't bleed through. */
const PNL_GRID_BUDGET = '180px 1fr 1fr 1fr 1fr 90px 90px';
const PNL_GRID_NO_BUDGET = '180px 1fr 110px';
const PNL_MIN_WIDTH_BUDGET = 900;    // px — below this mobile horiz-scrolls
const PNL_MIN_WIDTH_NO_BUDGET = 420;

// Shared styling fragments — kept here so AdminPnL and ManagerPnL stay in sync.
const STICKY_FIRST_COL_DEFAULT_BG = '#FFFFFF';
const STICKY_FIRST_COL_HIGHLIGHT_BG = '#F9FAFB';
const STICKY_HEADER_BG = '#E7EDE8';
const STICKY_SECTION_BG = '#F3F5F2';

function PnlLineRow({ label, actual, budget, indent, bold, isProfit, isSubtotal, pctUsed, costPct, onPctChange, editable, highlight, showBudgetCols, accrued }) {
  const hasBudget = budget && budget.month_budget > 0;
  const variance = hasBudget ? actual - budget.mtd_target : null;
  const profitColor = isProfit ? (actual >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]') : '';
  const isRevenueLine = label.toLowerCase().includes('revenue') || label.toLowerCase().includes('sale');
  const varianceColor = variance != null ? (
    isProfit || isRevenueLine
      ? (variance >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]')
      : (variance <= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]')
  ) : '';
  const costPctColor = costPct != null ? (
    isProfit ? (costPct >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]')
    : (costPct > 30 ? 'text-[#C45A41]' : costPct > 20 ? 'text-[#D4A017]' : 'text-[#2D6A4F]')
  ) : '';

  const rowBg = highlight ? STICKY_FIRST_COL_HIGHLIGHT_BG : STICKY_FIRST_COL_DEFAULT_BG;
  const cols = showBudgetCols ? PNL_GRID_BUDGET : PNL_GRID_NO_BUDGET;
  // ITER236 — Fixed-cost "actuals" are accrued estimates, NOT measured spend.
  // Communicate that visually so leaders don't mistake the number for receipt-
  // backed truth. See investigation in PRD/ITER236.
  const actualTooltip = accrued
    ? `Accrued (est.): ${fmt(actual)} — computed from standing amount × elapsed-days × pct_used. Not from receipts.`
    : null;
  // Every numeric span uses these classes to guarantee single-line, ellipsis-
  // truncated, right-aligned rendering that cannot overflow its grid cell.
  const numCls = 'text-right tabular-nums whitespace-nowrap overflow-hidden text-ellipsis min-w-0';
  // The first column (label) is pinned to the left edge of the scroll container
  // with a solid background + right border so scrolled content can't bleed.
  const stickyCell = {
    position: 'sticky',
    left: 0,
    zIndex: 10,
    backgroundColor: rowBg,
    boxShadow: 'inset -1px 0 0 0 rgba(226, 232, 223, 0.9)',
    paddingLeft: indent ? 20 : 8,
    paddingRight: 8,
  };

  return (
    <div className={`grid items-center text-[13px] py-1.5 gap-x-2 ${bold ? 'font-semibold' : ''} ${isSubtotal ? 'border-t border-[#E2E8DF]' : 'border-b border-[#E2E8DF]/40'} ${isProfit ? `${profitColor} font-bold` : ''}`}
      style={{ gridTemplateColumns: cols, backgroundColor: rowBg }}>
      <span
        className={`truncate min-w-0 ${isProfit ? '' : 'text-[color:var(--color-text-muted)]'}`}
        style={stickyCell}
        title={label}>{label}</span>
      <span className={`${numCls} ${isProfit ? '' : 'text-[color:var(--color-text-primary)] font-medium'}`}
        title={actualTooltip || undefined}
        data-accrued={accrued ? '1' : undefined}>
        {fmt(actual)}
        {accrued && (
          <span className="text-[9px] font-semibold uppercase tracking-wider text-[#C45A41]/70 ml-1 align-text-top">est.</span>
        )}
      </span>
      {showBudgetCols ? (
        <>
          <span className={`${numCls} text-[color:var(--color-text-muted)] text-[12px]`}>{hasBudget ? fmt(budget.month_budget) : ''}</span>
          <span className={`${numCls} text-[color:var(--color-text-muted)] text-[12px]`}>{hasBudget ? fmt(budget.mtd_target) : ''}</span>
          <span className={`${numCls} text-[12px] font-semibold ${varianceColor}`}>
            {variance != null ? `${variance >= 0 ? '+' : ''}${fmt(variance)}` : ''}
          </span>
          <span className={`${numCls} text-[12px] font-semibold ${costPctColor}`}>
            {costPct != null ? `${costPct}%` : ''}
          </span>
          {editable && onPctChange ? (
            <div className="flex items-center justify-end gap-0.5 min-w-0 pr-2">
              <input type="number" min="0" max="200" step="5"
                value={pctUsed ?? 100}
                onChange={e => onPctChange(parseInt(e.target.value) || 0)}
                className="w-[44px] h-7 text-right text-[12px] font-bold tabular-nums border-2 border-[color:var(--color-primary)]/30 rounded bg-white focus:border-[color:var(--color-primary)] focus:outline-none text-[color:var(--color-text-primary)]"
                data-testid={`pct-used-${label.toLowerCase().replace(/\s/g, '-')}`}
              />
              <span className="text-[10px] font-bold text-[color:var(--color-text-muted)]">%</span>
            </div>
          ) : (
            <span className={`${numCls} text-[12px] font-bold text-[color:var(--color-text-primary)] pr-2`}>
              {pctUsed != null ? `${pctUsed}%` : ''}
            </span>
          )}
        </>
      ) : (
        <span className={`${numCls} text-[12px] font-semibold ${costPctColor} pr-2`}>
          {costPct != null ? `${costPct}%` : ''}
        </span>
      )}
    </div>
  );
}

function PnlSectionHeader({ label, subtitle, subtitleTooltip }) {
  // Section header bar — full-width, sticky-left so the label stays visible
  // when the table is horizontally scrolled. It is NOT sticky-top because
  // scrolling past a section is expected (only column headers pin to top).
  return (
    <div
      className="text-[10px] font-bold text-[color:var(--color-primary)] uppercase tracking-[0.08em] py-1.5 border-y-2 border-[color:var(--color-primary)]/20"
      style={{
        position: 'sticky',
        left: 0,
        zIndex: 12,
        backgroundColor: STICKY_SECTION_BG,
        paddingLeft: 8,
        paddingRight: 8,
      }}>
      {label}
      {subtitle && (
        <span
          className="ml-2 text-[9px] font-normal normal-case tracking-normal text-[color:var(--color-text-muted)] italic"
          title={subtitleTooltip || undefined}
          data-testid={`section-subtitle-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}>
          — {subtitle}
        </span>
      )}
    </div>
  );
}

function PnlColumnHeaders({ hasBudget }) {
  const cols = hasBudget ? PNL_GRID_BUDGET : PNL_GRID_NO_BUDGET;
  return (
    <div
      className="grid items-center text-[10px] uppercase tracking-wider text-[color:var(--color-text-primary)] font-bold py-1.5 gap-x-2 border-b-2 border-[color:var(--color-primary)]/30 shadow-sm"
      style={{
        gridTemplateColumns: cols,
        position: 'sticky',
        top: 0,
        zIndex: 20,
        backgroundColor: STICKY_HEADER_BG,
      }}
      data-testid="pnl-header-row">
      <span
        style={{
          position: 'sticky',
          left: 0,
          zIndex: 25,               // Top-left corner wins every overlap
          backgroundColor: STICKY_HEADER_BG,
          paddingLeft: 8,
          paddingRight: 8,
          boxShadow: 'inset -1px 0 0 0 rgba(20, 61, 48, 0.15)',
        }}>Line Item</span>
      <span className="text-right">Actual MTD</span>
      {hasBudget ? (
        <>
          <span className="text-right">Month Bgt</span>
          <span className="text-right">MTD Target</span>
          <span className="text-right">Variance</span>
          <span className="text-right">Cost %</span>
          <span className="text-right pr-2">% Used</span>
        </>
      ) : (
        <span className="text-right pr-2">Cost %</span>
      )}
    </div>
  );
}

/* ITER234 — code-driven classification. The old name-guessing heuristic
 * (regex on key text + SYSTEM_EXACT set) has been replaced by the shared
 * registry in `src/lib/pnlRegistry.js`. The wrapper below preserves the
 * existing call-site signature so the render logic doesn't change. */
function isOperationalCostKey(key) {
  return isOperationalCostKeyV2(key);
}

/* Helper: lookup budget for a line item by name (case-insensitive) */
function getBudget(budgetItems, ...names) {
  if (!budgetItems || Object.keys(budgetItems).length === 0) return null;
  for (const name of names) {
    const key = name.toLowerCase().trim();
    if (budgetItems[key]) return budgetItems[key];
  }
  return null;
}

/* ITER235 — code-first budget lookup. Prefers `outlet.budget_by_code[code]`
 * (the ITER234 canonical map) and falls back to the legacy name-keyed
 * `getBudget()` so v1 budgets keep rendering. Both shapes carry
 * `{month_budget, mtd_target}` so the PnlLineRow render is unchanged. */
function bgtByCode(o, code, ...legacyNames) {
  const byCode = o?.budget_by_code?.[code];
  if (byCode) return byCode;
  return getBudget(o?.budget_line_items, ...legacyNames) || null;
}

function CostRow({ label, value, salesTotal, indent, highlightCost }) {
  const p = pctOf(value, salesTotal);
  const pctColor = highlightCost ? costColorPnL(parseFloat(p)) : 'text-[color:var(--color-text-muted)]';
  return (
    <div className={`flex items-center justify-between text-sm py-0.5 ${indent ? 'pl-3' : ''}`}>
      <span className="text-[color:var(--color-text-muted)] truncate flex-1 min-w-0">{label}</span>
      <span className="text-[color:var(--color-text-primary)] whitespace-nowrap ml-2 text-right">
        {fmt(value)}
        <span className={`text-[10px] ml-1 ${pctColor}`}>({p}%)</span>
      </span>
    </div>
  );
}

function ComparisonRow({ label, sysVal, finVal, indent }) {
  const vp = varPct(finVal, sysVal);
  return (
    <div className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-1 text-sm py-0.5 ${indent ? 'pl-3' : ''}`}>
      <span className="text-[color:var(--color-text-muted)] truncate min-w-0">{label}</span>
      <span className="text-[color:var(--color-text-muted)] text-xs w-20 text-right">{fmt(sysVal)}</span>
      <span className="text-[color:var(--color-text-primary)] font-medium w-20 text-right">{fmt(finVal)}</span>
      <span className="w-14 text-right">
        {vp != null ? (
          <span className={`text-[10px] font-medium ${Number(vp) >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
            {Number(vp) >= 0 ? '+' : ''}{vp}%
          </span>
        ) : <span className="text-[10px] text-[#9CA3AF]">—</span>}
      </span>
    </div>
  );
}

/* Finalize fields - Revenue + Variable + Fixed (NO profit here) */
const FINALIZE_REVENUE = [
  { key: 'net_sales', label: 'Net Sales' }, { key: 'food_sale', label: 'Food Sale' },
  { key: 'beverage_sale', label: 'Beverage Sale' }, { key: 'other_sale', label: 'Other Sale' },
];
const FINALIZE_VARIABLE = [
  { key: 'food_cost', label: 'Food Cost' }, { key: 'beverage_cost', label: 'Beverage Cost' },
  { key: 'other_purchases', label: 'Other Purchases' }, { key: 'maintenance_cost', label: 'Maintenance' },
];
const FINALIZE_FIXED = [
  { key: 'salary', label: 'Salary' }, { key: 'rent', label: 'Rent' },
  { key: 'marketing', label: 'Marketing' }, { key: 'utilities', label: 'Utilities' },
  { key: 'other_fixed', label: 'Other Fixed' },
];

/* ──────────────── MAIN COMPONENT ──────────────── */
export default function AdminPnL() {
  const { getHeaders, user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [pnlData, setPnlData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('mtd');
  const [asOfDate, setAsOfDate] = useState(null);
  const [asOfCalendarOpen, setAsOfCalendarOpen] = useState(false);
  // Outlet portfolio filter — narrows the MTD view to outlets within a portfolio.
  // Separate from the (now removed) P&L "Portfolio" tab; this is outlet-grouping.
  const [portfolioId, setPortfolioId] = useState('');
  // Page-level outlet selector. Empty string ('') means "All Outlets".
  const [outletId, setOutletId] = useState('');
  const isPastView = pnlData?.is_past_view === true;

  // Dialogs
  // ITER238 — Fixed Costs dialog state removed. Dialog deleted; collection is inert.
  const [saving, setSaving] = useState(false);
  const [finalizeDialog, setFinalizeDialog] = useState({ open: false, outlet: null });
  const [finalizeForm, setFinalizeForm] = useState({});
  const [finalizeCustomFields, setFinalizeCustomFields] = useState([]);
  const [finalizing, setFinalizing] = useState(false);
  const [confirmUnfinalize, setConfirmUnfinalize] = useState(null);
  // Override tracking
  const [gpOverride, setGpOverride] = useState(false);
  const [npOverride, setNpOverride] = useState(false);

  const [outlets, setOutlets] = useState([]);

  // P&L Template (legacy — kept for backward compat with existing template data)
  const [template, setTemplate] = useState(null);


  /* ─── Auto-calculated profit values ─── */
  const autoGrossProfit = useMemo(() => {
    const ns = parseFloat(finalizeForm.net_sales) || 0;
    const totalVar = (parseFloat(finalizeForm.food_cost) || 0)
      + (parseFloat(finalizeForm.beverage_cost) || 0)
      + (parseFloat(finalizeForm.other_purchases) || 0)
      + (parseFloat(finalizeForm.maintenance_cost) || 0);
    return ns - totalVar;
  }, [finalizeForm.net_sales, finalizeForm.food_cost, finalizeForm.beverage_cost, finalizeForm.other_purchases, finalizeForm.maintenance_cost]);

  const autoNetProfit = useMemo(() => {
    const gp = gpOverride ? (parseFloat(finalizeForm.gross_profit) || 0) : autoGrossProfit;
    const totalFixed = (parseFloat(finalizeForm.salary) || 0)
      + (parseFloat(finalizeForm.rent) || 0)
      + (parseFloat(finalizeForm.marketing) || 0)
      + (parseFloat(finalizeForm.utilities) || 0)
      + (parseFloat(finalizeForm.other_fixed) || 0)
      + finalizeCustomFields.reduce((s, cf) => s + (parseFloat(cf.value) || 0), 0);
    return gp - totalFixed;
  }, [gpOverride, autoGrossProfit, finalizeForm, finalizeCustomFields]);

  // Sync auto-values into form when not overridden
  useEffect(() => {
    if (!gpOverride && finalizeDialog.open) {
      setFinalizeForm(prev => ({ ...prev, gross_profit: autoGrossProfit }));
    }
  }, [autoGrossProfit, gpOverride, finalizeDialog.open]);

  useEffect(() => {
    if (!npOverride && finalizeDialog.open) {
      setFinalizeForm(prev => ({ ...prev, net_profit: autoNetProfit }));
    }
  }, [autoNetProfit, npOverride, finalizeDialog.open]);

  const gpPct = useMemo(() => {
    const ns = parseFloat(finalizeForm.net_sales) || 0;
    const gp = parseFloat(finalizeForm.gross_profit) || 0;
    return ns > 0 ? (gp / ns * 100).toFixed(1) : '0.0';
  }, [finalizeForm.net_sales, finalizeForm.gross_profit]);

  const npPct = useMemo(() => {
    const ns = parseFloat(finalizeForm.net_sales) || 0;
    const np = parseFloat(finalizeForm.net_profit) || 0;
    return ns > 0 ? (np / ns * 100).toFixed(1) : '0.0';
  }, [finalizeForm.net_sales, finalizeForm.net_profit]);

  /* ─── Data loading ─── */
  const loadPnL = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/pnl?year=${year}&month=${month}`;
      if (asOfDate) url += `&as_of_date=${format(asOfDate, 'yyyy-MM-dd')}`;
      if (portfolioId) url += `&portfolio_id=${portfolioId}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setPnlData(res.data);
    } catch { toast.error('Failed to load P&L data'); }
    finally { setLoading(false); }
  }, [year, month, asOfDate, portfolioId, getHeaders]);

  const loadOutlets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/outlets`, { headers: getHeaders() });
      setOutlets(res.data);
    } catch {}
  }, [getHeaders]);


  const loadTemplate = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pnl/template`, { headers: getHeaders() });
      setTemplate(res.data?.is_active ? res.data : null);
    } catch {}
  }, [getHeaders]);

  // ── Canonical convergence loaders ────────────────────────
  // /api/pnl/canonical/multi → per-outlet + consolidated canonical trees.
  // Drives Portfolio tab (consolidated) + MTD by Outlet canonical view.


  // Reconciliation validator — fire-and-forget, surfaces drift banner.

  // Canonical convergence loaders — fire per-tab.
  // Load outlets on mount so the page-level outlet selector (used by Review +
  // Meeting tabs) is populated immediately.
  useEffect(() => { loadOutlets(); }, [loadOutlets]);
  useEffect(() => { loadTemplate(); }, [loadTemplate]);
  // Re-load MTD whenever year / month / as-of / portfolio changes.
  useEffect(() => { loadPnL(); }, [loadPnL]);

  const saveToggles = async (outletId, toggles) => {
    try {
      await axios.put(`${API}/admin/pnl/template/toggles`, {
        outlet_id: outletId, year, month, toggles
      }, { headers: getHeaders() });
    } catch { toast.error('Failed to save toggle'); }
  };

  // Line item management
  const [lineDialog, setLineDialog] = useState({ open: false, outlet: null, items: [] });
  const [newLineName, setNewLineName] = useState('');
  const [newLineCategory, setNewLineCategory] = useState('expense');

  const openManageLines = async (outlet) => {
    try {
      const res = await axios.get(`${API}/admin/pnl/line-items/${outlet.outlet_id}`, { headers: getHeaders() });
      setLineDialog({ open: true, outlet, items: res.data || [] });
      setNewLineName('');
    } catch { toast.error('Failed to load line items'); }
  };

  const addLineItem = async () => {
    if (!newLineName.trim()) return;
    try {
      const res = await axios.post(`${API}/admin/pnl/line-items/${lineDialog.outlet.outlet_id}`, {
        name: newLineName.trim(), category: newLineCategory, type: 'fixed'
      }, { headers: getHeaders() });
      setLineDialog(prev => ({ ...prev, items: [...prev.items, res.data] }));
      setNewLineName('');
      toast.success('Line item added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
  };

  const toggleLineActive = async (item) => {
    try {
      await axios.put(`${API}/admin/pnl/line-items/${lineDialog.outlet.outlet_id}/${item.item_id}`, {
        active: !item.active
      }, { headers: getHeaders() });
      setLineDialog(prev => ({
        ...prev,
        items: prev.items.map(i => i.item_id === item.item_id ? { ...i, active: !i.active } : i)
      }));
    } catch { toast.error('Failed to update'); }
  };

  // % Used save handler
  const savePctUsed = async (outletId, values) => {
    try {
      await axios.post(`${API}/pnl/pct-used`, {
        outlet_id: outletId, year, month, values
      }, { headers: getHeaders() });
    } catch { /* silent — debounced save */ }
  };

  /* ─── Fixed cost actions ─── */
  // ITER238 — Fixed Costs dialog removed. fixed_cost_budgets is inert
  // as a P&L source; the GET/POST endpoints survive (deprecated, no UI).

  /* ─── Month lock ─── */
  const toggleMonthLock = async (outlet) => {
    if (outlet.finalized) { toast.error('Unfinalize first.'); return; }
    const action = outlet.month_locked ? 'month-unlock' : 'month-lock';
    try {
      await axios.post(`${API}/admin/${action}/${outlet.outlet_id}?year=${year}&month=${month}`, {}, { headers: getHeaders() });
      toast.success(outlet.month_locked ? 'Month unlocked' : 'Month locked');
      loadPnL();
    } catch { toast.error('Failed to toggle lock'); }
  };

  /* ─── Finalize ─── */
  const openFinalizeDialog = (outlet) => {
    const s = outlet.system || {};
    const bl = outlet.budget_line_items || {};
    const pu = outlet.pct_used || {};
    const totalRevenue = outlet.total_revenue || s.total_revenue || 0;
    const foodCost = outlet.food_cost || s.food_cost || 0;
    const bevCost = outlet.beverage_cost || s.beverage_cost || 0;
    const otherPurchases = outlet.other_purchases || s.other_purchases || 0;
    const maintCost = outlet.maintenance_cost || s.maintenance_cost || 0;
    const mktSpend = outlet.marketing_spend_mtd || 0;
    const totalCogs = foodCost + bevCost;
    const grossProfit = totalRevenue - totalCogs;

    const inactiveKeys = new Set((outlet.custom_line_items || []).filter(i => !i.active).map(i => i.name.toLowerCase().trim()));

    // Build finalize line items
    const lines = [];
    // Revenue
    lines.push({ key: 'food_net_revenue', label: 'Food Net Revenue', category: 'revenue', actual: s.food_sale || 0, system: true });
    lines.push({ key: 'bev_net_revenue', label: 'Beverage Net Revenue', category: 'revenue', actual: s.beverage_sale || 0, system: true });
    // ITER235 — zero-hide rule: include Room when either budget or actual is non-zero.
    const roomActual = Number(outlet.room_revenue || 0);
    const roomBgt = bgtByCode(outlet, 'R-ROOM', 'room net revenue', 'room revenue');
    const roomNonZero = roomActual > 0
      || Number(roomBgt?.month_budget || 0) > 0
      || Number(roomBgt?.mtd_target || 0) > 0;
    if (roomNonZero) {
      lines.push({ key: 'room_revenue', label: 'Room Net Revenue', category: 'revenue', actual: roomActual, system: true });
    }
    // COGS
    lines.push({ key: 'food_cost', label: 'Food Cost', category: 'cogs', actual: foodCost, system: true });
    lines.push({ key: 'bev_cost', label: 'Beverage Cost', category: 'cogs', actual: bevCost, system: true });
    // Operating — broken out actual MTD
    if (otherPurchases > 0) lines.push({ key: 'other_purchases', label: 'Other Purchases', category: 'expense', actual: otherPurchases, system: true });
    const sys = outlet.system || {};
    if (sys.electricity_cost > 0) lines.push({ key: 'electricity', label: 'Electricity', category: 'expense', actual: sys.electricity_cost, system: true });
    if (sys.gas_cost > 0) lines.push({ key: 'gas', label: 'Fuel / Gas', category: 'expense', actual: sys.gas_cost, system: true });
    if (sys.water_cost > 0) lines.push({ key: 'water', label: 'Water', category: 'expense', actual: sys.water_cost, system: true });
    if (sys.repairs_cost > 0) lines.push({ key: 'repairs', label: 'Maintenance & Repairs', category: 'expense', actual: sys.repairs_cost, system: true });
    if (mktSpend > 0) lines.push({ key: 'marketing', label: 'Marketing', category: 'expense', actual: mktSpend, system: true });
    // Fixed costs from budget
    let totalFixedActual = 0;
    Object.entries(bl).forEach(([bKey, bVal]) => {
      if (isOperationalCostKey(bKey) || inactiveKeys.has(bKey)) return;
      const pct = parseFloat(pu[bKey]) || 100;
      const actualMtd = Math.round(bVal.mtd_target * (pct / 100));
      if (bVal.month_budget === 0 && actualMtd === 0) return;
      lines.push({
        key: `fixed_${bKey}`, label: bKey.replace(/\b\w/g, c => c.toUpperCase()),
        category: 'fixed', actual: actualMtd, pct_used: pct, budget: bVal, system: false
      });
      totalFixedActual += actualMtd;
    });

    const totalExpenditure = totalCogs + otherPurchases + maintCost + mktSpend + totalFixedActual;
    const netProfit = totalRevenue - totalExpenditure;

    setFinalizeLines(lines.map(l => ({ ...l, edited: false })));
    setFinalizeNotes('');
    setFinalizeDialog({ open: true, outlet, totalRevenue, grossProfit, netProfit, totalCogs, totalExpenditure, totalFixedActual });
  };

  // Finalize line items state
  const [finalizeLines, setFinalizeLines] = useState([]);
  const [finalizeNotes, setFinalizeNotes] = useState('');

  const updateFinalizeLine = (idx, field, val) => {
    setFinalizeLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: val, edited: true } : l));
  };

  // Recompute finalize totals from lines
  const finalizeTotals = useMemo(() => {
    const rev = finalizeLines.filter(l => l.category === 'revenue').reduce((s, l) => s + (parseFloat(l.actual) || 0), 0);
    const cogs = finalizeLines.filter(l => l.category === 'cogs').reduce((s, l) => s + (parseFloat(l.actual) || 0), 0);
    const opex = finalizeLines.filter(l => l.category === 'expense').reduce((s, l) => s + (parseFloat(l.actual) || 0), 0);
    const fixed = finalizeLines.filter(l => l.category === 'fixed').reduce((s, l) => s + (parseFloat(l.actual) || 0), 0);
    const gp = rev - cogs;
    const totalExp = cogs + opex + fixed;
    const np = rev - totalExp;
    return { revenue: rev, cogs, opex, fixed, grossProfit: gp, totalExpenditure: totalExp, netProfit: np };
  }, [finalizeLines]);

  const submitFinalize = async () => {
    if (!finalizeNotes.trim()) {
      toast.error('Please add a note/reason for finalization');
      return;
    }
    setFinalizing(true);
    try {
      const outletData = finalizeDialog.outlet;
      // Build structured snapshot
      const snapshot_lines = finalizeLines.map(l => ({
        key: l.key, label: l.label, category: l.category,
        actual_mtd: parseFloat(l.actual) || 0,
        month_budget: l.budget?.month_budget || 0,
        mtd_target: l.budget?.mtd_target || 0,
        variance: (parseFloat(l.actual) || 0) - (l.budget?.mtd_target || 0),
        pct_used: l.pct_used ?? null,
        cost_pct: finalizeTotals.revenue > 0 ? parseFloat(((parseFloat(l.actual) || 0) / finalizeTotals.revenue * 100).toFixed(1)) : 0,
        system: l.system,
        edited: l.edited,
      }));
      const payload = {
        net_sales: finalizeTotals.revenue,
        food_sale: finalizeLines.find(l => l.key === 'food_net_revenue')?.actual || 0,
        beverage_sale: finalizeLines.find(l => l.key === 'bev_net_revenue')?.actual || 0,
        food_cost: finalizeLines.find(l => l.key === 'food_cost')?.actual || 0,
        beverage_cost: finalizeLines.find(l => l.key === 'bev_cost')?.actual || 0,
        other_purchases: finalizeLines.find(l => l.key === 'other_purchases')?.actual || 0,
        maintenance_cost: finalizeLines.find(l => l.key === 'maintenance')?.actual || 0,
        salary: 0, rent: 0, marketing: 0, utilities: 0, other_fixed: 0,
        gross_profit: finalizeTotals.grossProfit,
        net_profit: finalizeTotals.netProfit,
        notes: finalizeNotes,
        // Structured snapshot
        pnl_snapshot: snapshot_lines,
        budget_line_items: outletData?.budget_line_items || {},
        pct_used: Object.fromEntries(finalizeLines.filter(l => l.pct_used != null).map(l => [l.key.replace('fixed_', ''), l.pct_used])),
        marketing_spend_mtd: outletData?.marketing_spend_mtd || 0,
      };
      await axios.post(`${API}/admin/pnl/finalize/${outletData.outlet_id}?year=${year}&month=${month}`, payload, { headers: getHeaders() });
      toast.success(`P&L finalized for ${MONTHS[month - 1]} ${year}`);
      setFinalizeDialog({ open: false, outlet: null });
      loadPnL();
    } catch { toast.error('Failed to finalize P&L'); }
    finally { setFinalizing(false); }
  };

  const handleUnfinalize = async (outletId) => {
    try {
      await axios.post(`${API}/admin/pnl/unfinalize/${outletId}?year=${year}&month=${month}`, {}, { headers: getHeaders() });
      toast.success('Month unfinalized.');
      setConfirmUnfinalize(null);
      loadPnL();
    } catch { toast.error('Failed to unfinalize'); }
  };

  /* ─── Export removed (canonical engine deleted in iter180k) ─── */

  return (
    <div className="w-full" data-testid="admin-pnl-page">
      {/* Header — single compact row: title · filters · context · export */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mb-2" data-testid="pnl-header-bar">
        <h1 className="text-lg sm:text-xl font-semibold text-[color:var(--color-text-primary)] tracking-tight mr-1">Profit &amp; Loss</h1>
        <Select value={String(month)} onValueChange={v => { setMonth(parseInt(v)); setAsOfDate(null); }}>
          <SelectTrigger className="w-[88px] h-7 border-[#E2E8DF] text-xs px-2" data-testid="pnl-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={v => { setYear(parseInt(v)); setAsOfDate(null); }}>
          <SelectTrigger className="w-[72px] h-7 border-[#E2E8DF] text-xs px-2" data-testid="pnl-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Popover open={asOfCalendarOpen} onOpenChange={setAsOfCalendarOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={`h-7 px-2 text-xs border-[#E2E8DF] gap-1 ${asOfDate ? 'bg-[color:var(--color-primary)]/5 border-[color:var(--color-primary)]/30 text-[color:var(--color-primary)]' : 'text-[color:var(--color-text-muted)]'}`}
              data-testid="pnl-as-of-date-btn">
              <CalendarIcon className="w-3 h-3" />
              {asOfDate ? `as on ${format(asOfDate, 'dd MMM')}` : 'MTD as on…'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar mode="single" selected={asOfDate}
              onSelect={d => { setAsOfDate(d || null); setAsOfCalendarOpen(false); }}
              disabled={d => d > new Date() || d < new Date(year, month - 1, 1) || d > new Date(year, month, 0)}
              defaultMonth={new Date(year, month - 1)} />
            {asOfDate && (
              <div className="px-3 pb-2">
                <Button variant="ghost" size="sm" className="w-full h-7 text-xs text-[color:var(--color-text-muted)]"
                  onClick={() => { setAsOfDate(null); setAsOfCalendarOpen(false); }}>
                  Clear date filter
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
                {/* Outlet selector — applies to Review + Meeting Mode tabs.
            "All Outlets" → consolidated rollup; specific outlet → that
            outlet's exact uploaded budget structure (no merge mismatch). */}
        <select
          value={outletId}
          onChange={(e) => setOutletId(e.target.value)}
          className="h-7 px-2 text-[11px] border border-[#E2E8DF] rounded text-[color:var(--color-text-primary)] bg-white"
          data-testid="pnl-outlet-selector"
          aria-label="Outlet"
        >
          <option value="">All Outlets</option>
          {(outlets || [])
            .filter(o => !portfolioId || o.portfolio_id === portfolioId)
            .map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
        </select>
        {pnlData && (
          <span className="text-[11px] text-[color:var(--color-text-muted)] tabular-nums" data-testid="pnl-through-info">
            · Through {pnlData.data_through} · {pnlData.elapsed_days}/{pnlData.days_in_month}d
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {/* MTD Excel + PDF — uses shared pnlExporters reading the SAME
              outlet block already loaded in pnlData. No separate engine. */}
          <Button
            variant="outline" size="sm"
            disabled={!pnlData?.outlets?.length}
            onClick={() => {
              const list = outletId
                ? pnlData.outlets.filter((o) => o.outlet_id === outletId)
                : pnlData.outlets;
              if (!list.length) { toast.error('No outlet data to export'); return; }
              const monthLabel = `${MONTHS[month - 1]} ${year} · MTD`;
              const cols = list.map((o) => ({ label: o.outlet_name || o.outlet_id, block: o }));
              exportStatementXlsx({
                fileName: `PnL_MTD_${MONTHS[month - 1]}${year}.xlsx`,
                title: 'P&L Statement — MTD by Outlet',
                subtitle: monthLabel,
                columns: cols,
              });
            }}
            className="h-7 px-2 text-xs border-[#E2E8DF] text-[color:var(--color-primary)] gap-1"
            data-testid="mtd-export-xlsx-btn">
            <Download className="w-3 h-3" /> Excel
          </Button>
          <Button
            variant="outline" size="sm"
            disabled={!pnlData?.outlets?.length}
            onClick={() => {
              const list = outletId
                ? pnlData.outlets.filter((o) => o.outlet_id === outletId)
                : pnlData.outlets;
              if (!list.length) { toast.error('No outlet data to export'); return; }
              const cols = list.map((o) => ({ label: o.outlet_name || o.outlet_id, block: o }));
              exportStatementPdf({
                fileName: `PnL_MTD_${MONTHS[month - 1]}${year}.pdf`,
                title: 'P&L Statement — MTD by Outlet',
                subtitle: `${MONTHS[month - 1]} ${year} · MTD`,
                preparedBy: user?.name || user?.email || '',
                columns: cols,
              });
            }}
            className="h-7 px-2 text-xs border-[#E2E8DF] text-[color:var(--color-primary)] gap-1"
            data-testid="mtd-export-pdf-btn">
            <FileText className="w-3 h-3" /> PDF
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#F3F5F2] mb-2 h-7">
          <TabsTrigger value="mtd" className="text-[11px] h-6 px-2.5 data-[state=active]:bg-white" data-testid="tab-mtd">MTD by Outlet</TabsTrigger>
          <TabsTrigger value="review" className="text-[11px] h-6 px-2.5 data-[state=active]:bg-white" data-testid="tab-review">Review &amp; Compare</TabsTrigger>
        </TabsList>

        {/* ─── MTD TAB ─── */}
        <TabsContent value="mtd">
          {loading && <p className="text-center text-[color:var(--color-text-muted)] py-8">Loading...</p>}
          {isPastView && (
            <div className="mb-4 px-3 py-2 bg-[#D4A017]/10 border border-[#D4A017]/20 rounded-lg flex items-center gap-2 text-sm text-[color:var(--color-text-muted)]"
              data-testid="past-view-banner">
              <Lock className="w-4 h-4 text-[#D4A017]" />
              <span>Viewing MTD as on <strong className="text-[color:var(--color-text-primary)]">{pnlData?.as_of_date}</strong> — Read-only mode</span>
            </div>
          )}
          {/* Mobile: existing accordion (lg:hidden — preserved untouched) */}
          <div className="space-y-5 lg:hidden" data-testid="pnl-mobile-list">
            {pnlData?.outlets?.map(o => <OutletPnlCard key={o.outlet_id} o={o} pnlData={pnlData}
              onFinalize={openFinalizeDialog} onUnfinalize={setConfirmUnfinalize} onToggleLock={toggleMonthLock}
              readOnly={isPastView} template={pnlData?.template} onSaveToggles={saveToggles}
              onManageLines={openManageLines} onSavePctUsed={savePctUsed} />)}
          </div>

          {/* Desktop: left sidebar + full-width statement (hidden on mobile) */}
          <div className="hidden lg:block">
            {pnlData?.outlets?.length > 0 && (
              <DesktopPnLSplit
                outlets={pnlData.outlets}
                pnlData={pnlData}
                onFinalize={openFinalizeDialog}
                onUnfinalize={setConfirmUnfinalize}
                onToggleLock={toggleMonthLock}
                onManageLines={openManageLines}
                onSavePctUsed={savePctUsed}
                readOnly={isPastView}
              />
            )}
          </div>
        </TabsContent>

        {/* ─── REVIEW & COMPARE TAB (iter180l) ─── */}
        <TabsContent value="review">
          <ReviewAndCompare />
        </TabsContent>
      </Tabs>

      {/* ITER238 — Fixed Costs dialog deleted; collection is inert as a P&L source. */}

      {/* ─── Dynamic Finalize Dialog ─── */}
      <Dialog open={finalizeDialog.open} onOpenChange={open => !open && setFinalizeDialog({ open: false, outlet: null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="finalize-dialog">
          <DialogHeader>
            <DialogTitle className="text-[color:var(--color-text-primary)] flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-[#2D6A4F]" /> Finalize P&L — {finalizeDialog.outlet?.outlet_name}
            </DialogTitle>
            <DialogDescription className="text-sm text-[color:var(--color-text-muted)]">
              {MONTHS[month - 1]} {year} — Review and confirm. Edited values are tracked for audit.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1 py-2">
            <div className="grid items-center text-[9px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold py-1 px-1 bg-[#F3F5F2] rounded"
              style={{ gridTemplateColumns: '2fr 100px 80px 80px 70px 52px' }}>
              <span>Line Item</span>
              <span className="text-right">Actual MTD</span>
              <span className="text-right">Month Bgt</span>
              <span className="text-right">MTD Target</span>
              <span className="text-right">Cost %</span>
              <span className="text-right">% Used</span>
            </div>

            {['revenue', 'cogs', 'expense', 'fixed'].map(cat => {
              const catLines = finalizeLines.map((l, idx) => ({ ...l, idx })).filter(l => l.category === cat);
              if (catLines.length === 0) return null;
              const catLabel = { revenue: 'Revenue', cogs: 'Cost of Goods Sold', expense: 'Operating Expenses', fixed: 'Fixed Costs' }[cat];
              return (
                <div key={cat}>
                  <div className="text-[10px] font-bold text-[color:var(--color-primary)] uppercase tracking-wider pt-2 pb-0.5 border-b-2 border-[color:var(--color-primary)]/20">{catLabel}</div>
                  {catLines.map(line => {
                    const costPct = finalizeTotals.revenue > 0 ? parseFloat(((parseFloat(line.actual) || 0) / finalizeTotals.revenue * 100).toFixed(1)) : 0;
                    return (
                      <div key={line.key} className={`grid items-center text-[13px] py-1 px-1 ${line.edited ? 'bg-amber-50/60' : ''}`}
                        style={{ gridTemplateColumns: '2fr 100px 80px 80px 70px 52px' }}>
                        <span className="text-[color:var(--color-text-muted)] truncate flex items-center gap-1">
                          {line.label}
                          {line.edited && <span className="text-[8px] bg-amber-100 text-amber-700 px-1 rounded font-semibold">Edited</span>}
                        </span>
                        <div className="flex justify-end">
                          <input type="number" step="any"
                            value={line.actual}
                            onChange={e => updateFinalizeLine(line.idx, 'actual', parseFloat(e.target.value) || 0)}
                            className={`w-[90px] h-7 text-right text-[12px] font-medium tabular-nums border rounded px-1.5 ${
                              line.edited ? 'border-amber-400 bg-amber-50' : 'border-[#E2E8DF] bg-white'
                            } focus:border-[color:var(--color-primary)] focus:outline-none`}
                            data-testid={`fin-actual-${line.key}`}
                          />
                        </div>
                        <span className="text-right text-[11px] text-[color:var(--color-text-muted)] tabular-nums">{line.budget ? fmt(line.budget.month_budget) : '—'}</span>
                        <span className="text-right text-[11px] text-[color:var(--color-text-muted)] tabular-nums">{line.budget ? fmt(line.budget.mtd_target) : '—'}</span>
                        <span className={`text-right text-[11px] font-semibold tabular-nums ${costPct > 30 ? 'text-[#C45A41]' : costPct > 20 ? 'text-[#D4A017]' : 'text-[#2D6A4F]'}`}>
                          {costPct}%
                        </span>
                        <span className="text-right text-[11px] font-bold tabular-nums text-[color:var(--color-text-primary)]">
                          {line.pct_used != null ? `${line.pct_used}%` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            <div className="border-t-2 border-[color:var(--color-primary)] pt-2 mt-2 space-y-1">
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-[color:var(--color-text-muted)]">Total Revenue</span>
                <span className="text-[color:var(--color-text-primary)]">{fmt(finalizeTotals.revenue)}</span>
              </div>
              <div className={`flex justify-between text-sm font-bold ${finalizeTotals.grossProfit >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                <span>Gross Profit ({finalizeTotals.revenue > 0 ? ((finalizeTotals.grossProfit / finalizeTotals.revenue) * 100).toFixed(1) : '0'}%)</span>
                <span>{fmt(finalizeTotals.grossProfit)}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-[color:var(--color-text-muted)]">Total Expenditure</span>
                <span className="text-[color:var(--color-text-primary)]">{fmt(finalizeTotals.totalExpenditure)}</span>
              </div>
              <div className={`flex justify-between text-base font-bold ${finalizeTotals.netProfit >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                <span>Net Profit ({finalizeTotals.revenue > 0 ? ((finalizeTotals.netProfit / finalizeTotals.revenue) * 100).toFixed(1) : '0'}%)</span>
                <span>{fmt(finalizeTotals.netProfit)}</span>
              </div>
            </div>

            <div className="mt-3 pt-2 border-t border-[#E2E8DF]">
              <Label className="text-xs text-[color:var(--color-text-muted)] font-semibold">Notes / Reason for Finalization *</Label>
              <textarea value={finalizeNotes} onChange={e => setFinalizeNotes(e.target.value)}
                placeholder="e.g., Month-end review complete, all actuals verified..."
                className="w-full mt-1 p-2 border border-[#E2E8DF] rounded-md text-sm min-h-[60px] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                data-testid="finalize-notes" />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFinalizeDialog({ open: false, outlet: null })}
              className="border-[#E2E8DF] text-[color:var(--color-text-muted)]" data-testid="finalize-cancel">Cancel</Button>
            <Button onClick={submitFinalize} disabled={finalizing || !finalizeNotes.trim()}
              className="bg-[#2D6A4F] hover:bg-[color:var(--color-primary-hover)] text-white gap-1" data-testid="finalize-confirm">
              <FileCheck className="w-4 h-4" />
              {finalizing ? 'Finalizing...' : 'Confirm & Finalize'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Unfinalize Confirmation */}
      <AlertDialog open={!!confirmUnfinalize} onOpenChange={() => setConfirmUnfinalize(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unfinalize this month?</AlertDialogTitle>
            <AlertDialogDescription>This reverts to system-calculated values. Finalized data is kept but inactive.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unfinalize-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleUnfinalize(confirmUnfinalize)}
              className="bg-[#C45A41] hover:bg-[#A04A35] text-white" data-testid="unfinalize-confirm">Unfinalize</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Statement dialog removed in iter180k — Statements feature deleted. */}

      {/* ─── Line Item Management Dialog ─── */}
      <Dialog open={lineDialog.open} onOpenChange={o => !o && setLineDialog({ open: false, outlet: null, items: [] })}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="line-items-dialog">
          <DialogHeader>
            <DialogTitle className="text-[color:var(--color-text-primary)]">P&L Line Items — {lineDialog.outlet?.outlet_name}</DialogTitle>
            <DialogDescription className="text-sm text-[color:var(--color-text-muted)]">
              Add or deactivate line items. Inactive items are hidden from P&L but data is preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {lineDialog.items.length > 0 ? lineDialog.items.map(item => (
              <div key={item.item_id} className={`flex items-center justify-between p-2 rounded-lg border ${item.active ? 'border-[#E2E8DF] bg-white' : 'border-[#E2E8DF] bg-[#F9F9F8] opacity-60'}`}>
                <div>
                  <span className="text-sm text-[color:var(--color-text-primary)]">{item.name}</span>
                  <span className="text-[10px] text-[color:var(--color-text-muted)] ml-2 capitalize">{item.category} · {item.type}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => toggleLineActive(item)}
                  className={`h-6 text-[10px] ${item.active ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`}
                  data-testid={`toggle-line-${item.item_id}`}>
                  {item.active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            )) : (
              <p className="text-xs text-[color:var(--color-text-muted)] text-center py-3">No custom line items. Add below.</p>
            )}
            <div className="border-t border-[#E2E8DF] pt-2 mt-2">
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-[color:var(--color-text-muted)]">New Line Item</Label>
                  <Input placeholder="e.g. Staff Welfare" value={newLineName}
                    onChange={e => setNewLineName(e.target.value)}
                    className="h-8 text-sm border-[#E2E8DF]" data-testid="new-line-name" />
                </div>
                <Select value={newLineCategory} onValueChange={setNewLineCategory}>
                  <SelectTrigger className="w-28 h-8 text-xs border-[#E2E8DF]" data-testid="new-line-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="cogs">COGS</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={addLineItem} disabled={!newLineName.trim()}
                  className="h-8 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-xs"
                  data-testid="add-line-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ──────────────── HELPERS FOR TEMPLATE ITEMS ──────────────── */
const SYSTEM_VALUE_MAP = {
  net_sales: (o) => o.total_sales,
  food_sale: (o) => o.food_sale,
  beverage_sale: (o) => o.beverage_sale,
  other_sale: (o) => 0,
  room_revenue: (o) => o.room_revenue || 0,
  food_cost: (o) => o.food_cost,
  beverage_cost: (o) => o.beverage_cost,
  other_purchases: (o) => o.other_purchases,
  maintenance_cost: (o) => o.maintenance_cost,
};

const FIXED_KEYS = ['salary', 'rent', 'marketing', 'utilities', 'other_fixed'];

function getItemValue(item, o, pnlData) {
  const dk = item.data_key;
  if (!dk) return 0;
  if (SYSTEM_VALUE_MAP[dk]) return SYSTEM_VALUE_MAP[dk](o) || 0;
  if (FIXED_KEYS.includes(dk)) {
    const monthly = o.fixed_costs?.[dk] || 0;
    return Math.round(monthly / (pnlData?.days_in_month || 30) * (pnlData?.elapsed_days || 0));
  }
  return 0;
}

function ToggleBadge({ isApply, onClick }) {
  return (
    <button onClick={e => { e.stopPropagation(); onClick(); }}
      className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full transition-colors ${
        isApply ? 'bg-[#2D6A4F]/10 text-[#2D6A4F] hover:bg-[#2D6A4F]/20' : 'bg-[#9CA3AF]/10 text-[#9CA3AF] hover:bg-[#9CA3AF]/20'
      }`} data-testid={`toggle-badge-${isApply ? 'apply' : 'na'}`}>
      {isApply ? <ToggleRight className="w-2.5 h-2.5" /> : <ToggleLeft className="w-2.5 h-2.5" />}
      {isApply ? 'APPLY' : 'NA'}
    </button>
  );
}

/* ──────────────── PURE P&L STATEMENT BODY ────────────────
   Renders the structured (or finalized comparison) P&L table for a single
   outlet. NO Card chrome — caller is responsible for outer container.
   Used by both:
     1. OutletPnlCard (mobile accordion expanded body)
     2. DesktopPnLSplit (desktop right panel)
   ──────────────────────────────────────────────────────── */
function PnLStatementBody({ o, pnlData, localPctUsed, onPctChange }) {
  const sys = o.system || {};
  const fin = o.final || null;
  const isFinalized = o.finalized === true;

  if (isFinalized && fin) {
    return (
      <div className="space-y-0.5">
        <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-1 text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)] font-medium pb-1 border-b border-[#E2E8DF] sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
          <span>Line Item</span>
          <span className="w-20 text-right">System</span>
          <span className="w-20 text-right">Final</span>
          <span className="w-14 text-right">Var %</span>
        </div>
        <ComparisonRow label="Net Sales" sysVal={sys.net_sales} finVal={fin.net_sales} />
        <div className="border-t border-[#E2E8DF] pt-0.5 mt-0.5">
          <p className="text-[10px] font-medium text-[color:var(--color-text-muted)] uppercase tracking-wide mb-0.5">Variable Costs</p>
          <ComparisonRow label={`Food Cost (${pctOf(fin.food_cost, fin.net_sales)}%)`} sysVal={sys.food_cost} finVal={fin.food_cost} indent />
          <ComparisonRow label={`Bev Cost (${pctOf(fin.beverage_cost, fin.net_sales)}%)`} sysVal={sys.beverage_cost} finVal={fin.beverage_cost} indent />
          <ComparisonRow label={`Other (${pctOf(fin.other_purchases, fin.net_sales)}%)`} sysVal={sys.other_purchases} finVal={fin.other_purchases} indent />
          <ComparisonRow label={`Maintenance (${pctOf(fin.maintenance_cost, fin.net_sales)}%)`} sysVal={sys.maintenance_cost} finVal={fin.maintenance_cost} indent />
        </div>
        <div className="border-t border-[#E2E8DF] pt-0.5">
          <p className="text-[10px] font-medium text-[color:var(--color-text-muted)] uppercase tracking-wide mb-0.5">Fixed Costs</p>
          {DEFAULT_FIXED.map(ff => (
            <ComparisonRow key={ff.key} label={`${ff.label} (${pctOf(fin[ff.key], fin.net_sales)}%)`}
              sysVal={Math.round((o.fixed_costs?.[ff.key] || 0) / (pnlData?.days_in_month || 30) * (pnlData?.elapsed_days || 0))}
              finVal={fin[ff.key]} indent />
          ))}
        </div>
        <div className={`flex justify-between text-sm font-medium pt-0.5 border-t border-[#E2E8DF] ${fin.gross_profit >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
          <span className="flex items-center gap-1">{fin.gross_profit >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} GP ({fin.gross_margin_pct}%)</span>
          <span>{fmt(fin.gross_profit)}</span>
        </div>
        <div className={`flex justify-between text-sm font-bold pt-0.5 border-t-2 border-[color:var(--color-primary)] ${fin.net_profit >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
          <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> NP ({fin.net_margin_pct}%)</span>
          <span>{fmt(fin.net_profit)}</span>
        </div>
      </div>
    );
  }

  // ─── Structured (live) P&L statement ───
  const bl = o.budget_line_items || {};
  const hasBgt = Object.keys(bl).length > 0;
  const totalRevenue = o.total_revenue || sys.total_revenue || 0;
  const foodCost = o.food_cost || sys.food_cost || 0;
  const bevCost = o.beverage_cost || sys.beverage_cost || 0;
  const totalCogs = foodCost + bevCost;
  const grossProfit = totalRevenue - totalCogs;
  const inactiveKeys = new Set((o.custom_line_items || []).filter(i => !i.active).map(i => i.name.toLowerCase().trim()));

  const getSystemActualForKey = (key) => {
    const k = key.toLowerCase().trim();
    if (/\belectricity\b/.test(k)) return sys.electricity_cost || 0;
    if (/\bfuel\b|\bgas\b|\bcharcoal\b|\bdiesel\b|\bpetrol\b|\bwooden\b/.test(k)) return sys.gas_cost || 0;
    if (/\bwater\b/.test(k)) return sys.water_cost || 0;
    if (/\brepair\b|\bmaintenance\b/.test(k)) return sys.repairs_cost || (o.maintenance_cost || sys.maintenance_cost || 0);
    if (/\bmarketing\b/.test(k)) return o.marketing_spend_mtd || 0;
    if (/\bother purchas|\bstore issu/.test(k)) return o.other_purchases || sys.other_purchases || 0;
    return 0;
  };

  /* ITER234 — code-driven skip check replaces the inline SKIP_KEYS set.
   * `isSkipKeyV2(name)` returns true for any name that resolves (via
   * the alias bridge) to a CALC / REVENUE / COGS code. */
  const SKIP_KEYS = { has: (k) => isSkipKeyV2(k) };

  const opExpItems = [];
  const fixedItems = [];
  let totalOpExp = 0;
  let totalFixedActual = 0;
  let totalFixedBudget = 0;
  let totalFixedMtdTarget = 0;

  // ─── Subtotal budgets — computed by summing the SAME detail rows shown in the
  // P&L view (per user spec: "Total = sum of rows; do not use a separate Total
  // budget line that may be inconsistent"). Each subtotal also stays consistent
  // with its actual which is already computed as row-sums.
  // ITER235 — sum by registry code so v2 budgets (where bl is keyed by the
  // canonical label "beverage net revenue") AND v1 legacy budgets both
  // contribute. `bgtByCode(o, code, ...legacyNames)` walks budget_by_code first
  // then falls back to the legacy name-keyed dict.
  const sumBudgetsByCodes = (codeLegacyPairs) => {
    const looked = codeLegacyPairs
      .map(([code, ...legacy]) => bgtByCode(o, code, ...legacy))
      .filter(Boolean);
    if (looked.length === 0) return null;
    return {
      month_budget: looked.reduce((s, b) => s + (b.month_budget || 0), 0),
      mtd_target:   looked.reduce((s, b) => s + (b.mtd_target   || 0), 0),
    };
  };
  const revenueBudgetTotal = sumBudgetsByCodes([
    ['R-FOOD', 'food net revenue', 'food sale', 'food revenue'],
    ['R-BEV',  'beverage net revenue', 'liquor & beverage net revenue', 'beverage sale', 'beverage revenue', 'bev sale'],
    ['R-ROOM', 'room net revenue', 'room revenue'],
  ]);
  const cogsBudgetTotal = sumBudgetsByCodes([
    ['C-FOOD', 'food cost'],
    ['C-BEV',  'beverage cost', 'liquor & bev cost', 'bev cost'],
  ]);
  const grossProfitBudget = (revenueBudgetTotal && cogsBudgetTotal) ? {
    month_budget: (revenueBudgetTotal.month_budget || 0) - (cogsBudgetTotal.month_budget || 0),
    mtd_target:   (revenueBudgetTotal.mtd_target   || 0) - (cogsBudgetTotal.mtd_target   || 0),
  } : null;

  const seenOpPatterns = new Set();
  const tagPattern = (k) => {
    if (/\belectricity\b/.test(k)) seenOpPatterns.add('electricity');
    if (/\bfuel\b|\bgas\b|\bcharcoal\b|\bdiesel\b|\bpetrol\b|\bwooden\b/.test(k)) seenOpPatterns.add('gas');
    if (/\bwater\b/.test(k)) seenOpPatterns.add('water');
    if (/\brepair\b|\bmaintenance\b/.test(k)) seenOpPatterns.add('repairs');
    if (/\bmarketing\b/.test(k)) seenOpPatterns.add('marketing');
    if (/\bother purchas|\bstore issu/.test(k)) seenOpPatterns.add('other_purchases');
  };

  if (hasBgt) {
    Object.entries(bl).forEach(([key, bval]) => {
      if (SKIP_KEYS.has(key) || inactiveKeys.has(key)) return;
      const label = key.replace(/\b\w/g, c => c.toUpperCase());
      if (isOperationalCostKey(key)) {
        const actual = getSystemActualForKey(key);
        opExpItems.push({ key, label, actual, budget: bval });
        totalOpExp += actual;
        tagPattern(key.toLowerCase().trim());
      } else {
        const pct = parseFloat(localPctUsed?.[key] ?? 100);
        const actualMtd = Math.round((bval.mtd_target || 0) * (pct / 100));
        // ITER236 — flag accrued so the row renders the (est.) badge + tooltip
        fixedItems.push({ key, label, actual: actualMtd, budget: bval, pct_used: pct, accrued: true });
        totalFixedActual += actualMtd;
        totalFixedBudget += bval.month_budget || 0;
        totalFixedMtdTarget += bval.mtd_target || 0;
      }
    });
  } else {
    // ITER236 — marketing dedup is enforced on the backend (sets
    // `o.fixed_costs.marketing = 0` when live `marketing_spend_mtd > 0`),
    // so the DEFAULT_FIXED loop here will naturally skip the Marketing
    // row (prorated === 0 → return). The live spend is rendered exactly
    // once via the SUPPLEMENTAL_OP `_sup_marketing` line further down.
    DEFAULT_FIXED.forEach(ff => {
      if (inactiveKeys.has(ff.label.toLowerCase())) return;
      const prorated = Math.round((o.fixed_costs?.[ff.key] || 0) / (pnlData?.days_in_month || 30) * (pnlData?.elapsed_days || 0));
      if (prorated === 0) return;
      // accrued: all DEFAULT_FIXED rows are standing-amount × elapsed-days
      fixedItems.push({ key: ff.key, label: ff.label, actual: prorated, budget: null, pct_used: null, accrued: true });
      totalFixedActual += prorated;
    });
  }

  const SUPPLEMENTAL_OP = [
    { key: '_sup_other_purchases', label: 'Other Purchases / Store Issues', actual: o.other_purchases || sys.other_purchases || 0, pattern: 'other_purchases' },
    { key: '_sup_electricity', label: 'Electricity', actual: sys.electricity_cost || 0, pattern: 'electricity' },
    { key: '_sup_gas', label: 'Fuel / Gas', actual: sys.gas_cost || 0, pattern: 'gas' },
    { key: '_sup_water', label: 'Water', actual: sys.water_cost || 0, pattern: 'water' },
    { key: '_sup_repairs', label: 'Maintenance & Repairs', actual: sys.repairs_cost || 0, pattern: 'repairs' },
    { key: '_sup_marketing', label: 'Marketing', actual: o.marketing_spend_mtd || 0, pattern: 'marketing' },
  ];
  SUPPLEMENTAL_OP.forEach(sup => {
    if (seenOpPatterns.has(sup.pattern)) return;
    if ((sup.actual || 0) <= 0) return;
    opExpItems.push({ key: sup.key, label: sup.label, actual: sup.actual, budget: null });
    totalOpExp += sup.actual;
  });

  const totalExpenditure = totalCogs + totalOpExp + totalFixedActual;
  const netProfit = totalRevenue - totalExpenditure;

  // Subtotal budgets that depend on dynamically-built item lists
  const opExpBudgetTotal = (() => {
    const items = opExpItems.filter(i => i.budget);
    if (items.length === 0) return null;
    return {
      month_budget: items.reduce((s, i) => s + (i.budget.month_budget || 0), 0),
      mtd_target:   items.reduce((s, i) => s + (i.budget.mtd_target   || 0), 0),
    };
  })();
  const totalExpenditureBudget = (() => {
    if (!cogsBudgetTotal && !opExpBudgetTotal && totalFixedMtdTarget === 0) return null;
    return {
      month_budget: (cogsBudgetTotal?.month_budget || 0) + (opExpBudgetTotal?.month_budget || 0) + totalFixedBudget,
      mtd_target:   (cogsBudgetTotal?.mtd_target   || 0) + (opExpBudgetTotal?.mtd_target   || 0) + totalFixedMtdTarget,
    };
  })();
  const netProfitBudget = (revenueBudgetTotal && totalExpenditureBudget) ? {
    month_budget: (revenueBudgetTotal.month_budget || 0) - (totalExpenditureBudget.month_budget || 0),
    mtd_target:   (revenueBudgetTotal.mtd_target   || 0) - (totalExpenditureBudget.mtd_target   || 0),
  } : null;

  return (
    <>
      <div
        className="-mx-2 border border-[#E2E8DF] rounded-md bg-white"
        style={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }}
        data-testid={`pnl-statement-${o.outlet_id}`}>
        <div className="space-y-0" style={{ minWidth: hasBgt ? `${PNL_MIN_WIDTH_BUDGET}px` : `${PNL_MIN_WIDTH_NO_BUDGET}px`, width: '100%' }}>
          <PnlColumnHeaders hasBudget={hasBgt} />

          {/* ═══ REVENUE ═══ */}
          <PnlSectionHeader label="Revenue" />
          <PnlLineRow label="Food Net Revenue" actual={o.food_sale || sys.food_sale || 0}
            budget={bgtByCode(o, 'R-FOOD', 'food net revenue', 'food sale', 'food revenue')} indent showBudgetCols={hasBgt} />
          <PnlLineRow label="Beverage Net Revenue" actual={o.beverage_sale || sys.beverage_sale || 0}
            budget={bgtByCode(o, 'R-BEV', 'beverage net revenue', 'liquor & beverage net revenue', 'beverage sale', 'beverage revenue', 'bev sale')} indent showBudgetCols={hasBgt} />
          {(() => {
            // ITER235 — zero-hide rule: show Room row when budget or actual is non-zero.
            const roomBgt = bgtByCode(o, 'R-ROOM', 'room net revenue', 'room revenue');
            const roomActual = Number(o.room_revenue || 0);
            const roomMonth  = Number(roomBgt?.month_budget || 0);
            const roomMtd    = Number(roomBgt?.mtd_target   || 0);
            if (roomActual === 0 && roomMonth === 0 && roomMtd === 0) return null;
            return (
              <PnlLineRow label="Room Net Revenue" actual={roomActual}
                budget={roomBgt} indent showBudgetCols={hasBgt} />
            );
          })()}
          <PnlLineRow label="Total Revenue" actual={totalRevenue}
            budget={revenueBudgetTotal} bold isSubtotal highlight showBudgetCols={hasBgt} />

          {/* ═══ COST OF GOODS SOLD ═══ */}
          <PnlSectionHeader label="Cost of Goods Sold" />
          <PnlLineRow label="Food Cost" actual={foodCost}
            budget={bgtByCode(o, 'C-FOOD', 'food cost')} indent showBudgetCols={hasBgt}
            costPct={totalRevenue > 0 ? parseFloat(pctOf(foodCost, totalRevenue)) : null} />
          <PnlLineRow label="Beverage Cost" actual={bevCost}
            budget={bgtByCode(o, 'C-BEV', 'beverage cost', 'liquor & bev cost', 'bev cost')} indent showBudgetCols={hasBgt}
            costPct={totalRevenue > 0 ? parseFloat(pctOf(bevCost, totalRevenue)) : null} />
          <PnlLineRow label="Total COGS" actual={totalCogs}
            budget={cogsBudgetTotal} bold isSubtotal showBudgetCols={hasBgt}
            costPct={totalRevenue > 0 ? parseFloat(pctOf(totalCogs, totalRevenue)) : null} />

          {/* ═══ GROSS PROFIT ═══ */}
          <PnlLineRow label="Gross Profit" actual={grossProfit}
            budget={grossProfitBudget} bold isProfit isSubtotal highlight showBudgetCols={hasBgt}
            costPct={totalRevenue > 0 ? parseFloat(pctOf(grossProfit, totalRevenue)) : null} />

          {/* ═══ OPERATING EXPENSES ═══ */}
          {opExpItems.length > 0 && (
            <>
              <PnlSectionHeader label="Operating Expenses" />
              {opExpItems.map(item => (
                <PnlLineRow key={item.key} label={item.label} actual={item.actual}
                  budget={item.budget} indent showBudgetCols={hasBgt}
                  costPct={totalRevenue > 0 ? parseFloat(pctOf(item.actual, totalRevenue)) : null} />
              ))}
              <PnlLineRow label="Total Operating Expenses" actual={totalOpExp}
                budget={opExpBudgetTotal} bold isSubtotal showBudgetCols={hasBgt}
                costPct={totalRevenue > 0 ? parseFloat(pctOf(totalOpExp, totalRevenue)) : null} />
            </>
          )}

          {/* ═══ FIXED COSTS ═══ */}
          {fixedItems.length > 0 && (
            <>
              <PnlSectionHeader
                label="Fixed Costs"
                subtitle="Accrued (est.) — column is computed, not from receipts"
                subtitleTooltip="The Actual column here is derived from standing monthly amounts × elapsed-days × pct_used. It is an accrued estimate, not measured spend. Receipt-level reconciliation happens in the Finalize step." />
              {fixedItems.map(item => (
                <PnlLineRow key={item.key} label={item.label} actual={item.actual}
                  budget={item.budget} indent pctUsed={item.pct_used} showBudgetCols={hasBgt}
                  accrued={item.accrued}
                  costPct={totalRevenue > 0 ? parseFloat(pctOf(item.actual, totalRevenue)) : null}
                  editable={hasBgt && !isFinalized && !!onPctChange}
                  onPctChange={hasBgt && !isFinalized && onPctChange ? (val) => onPctChange(item.key, val) : undefined} />
              ))}
              <PnlLineRow label="Total Fixed Costs" actual={totalFixedActual}
                budget={hasBgt ? { month_budget: totalFixedBudget, mtd_target: totalFixedMtdTarget } : null}
                bold isSubtotal showBudgetCols={hasBgt}
                accrued
                costPct={totalRevenue > 0 ? parseFloat(pctOf(totalFixedActual, totalRevenue)) : null} />
            </>
          )}

          {/* ═══ TOTAL EXPENDITURE ═══ */}
          <PnlLineRow label="Total Expenditure" actual={totalExpenditure} bold isSubtotal showBudgetCols={hasBgt}
            budget={totalExpenditureBudget}
            costPct={totalRevenue > 0 ? parseFloat(pctOf(totalExpenditure, totalRevenue)) : null} />

          {/* ═══ NET PROFIT ═══ */}
          <div className="border-t-2 border-[color:var(--color-primary)] mt-1 pt-1 bg-[#F9FAFB] rounded-b" data-testid={`pnl-net-${o.outlet_id}`}>
            <PnlLineRow label="Net Profit / (Loss)" actual={netProfit}
              budget={netProfitBudget} bold isProfit showBudgetCols={hasBgt}
              costPct={totalRevenue > 0 ? parseFloat(pctOf(netProfit, totalRevenue)) : null} />
          </div>
        </div>
      </div>

      {!hasBgt && (
        <div className="mt-3 px-3 py-2 bg-[#F9F9F8] rounded-lg border border-[#E2E8DF]">
          <p className="text-[10px] text-[color:var(--color-text-muted)] text-center">
            No budget uploaded for this outlet. Upload from Organization → Outlets to see budget columns.
          </p>
        </div>
      )}

      <div className="mt-3 px-2 py-2 bg-[#F9F9F8] rounded-lg border border-[#E2E8DF]" data-testid={`mtd-progress-${o.outlet_id}`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-semibold text-[color:var(--color-text-muted)] uppercase tracking-wide">MTD Progress</span>
          <span className="text-xs font-bold text-[color:var(--color-text-primary)]">{pnlData?.elapsed_days || 0} of {pnlData?.days_in_month || 30} days</span>
        </div>
        <div className="w-full bg-[#E2E8DF] rounded-full h-1.5">
          <div className="bg-[#2D6A4F] h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, ((pnlData?.elapsed_days || 0) / (pnlData?.days_in_month || 30)) * 100)}%` }} />
        </div>
        <p className="text-[9px] text-[color:var(--color-text-muted)] mt-0.5">MTD Target based on calendar days elapsed (excluding today) · {o.days_reported} days with data entries</p>
      </div>
    </>
  );
}

/* ──────────────── DESKTOP SPLIT VIEW ────────────────
   Left sidebar (240px): scrollable outlet list with NP% KPI.
   Right panel: full-width P&L statement for selected outlet — no Card chrome,
   no max-width, no centered container. Excel-style edge-to-edge.
   Mobile (<lg) is intentionally hidden — accordion handles small screens.
   ──────────────────────────────────────────────────── */
function DesktopPnLSplit({ outlets, pnlData, onFinalize, onUnfinalize, onToggleLock,
  readOnly, onManageLines, onSavePctUsed }) {
  const [selectedId, setSelectedId] = useState(outlets[0]?.outlet_id || null);
  const [pctMap, setPctMap] = useState({}); // { [outletId]: { [key]: pct } }
  const pctTimerRef = useRef({});

  // Hydrate per-outlet pct_used from server data when outlets change
  useEffect(() => {
    const next = {};
    outlets.forEach(o => { next[o.outlet_id] = o.pct_used || {}; });
    setPctMap(next);
  }, [outlets]);

  // Keep selection valid if outlet list shrinks/changes
  useEffect(() => {
    if (!outlets.find(o => o.outlet_id === selectedId)) {
      setSelectedId(outlets[0]?.outlet_id || null);
    }
  }, [outlets, selectedId]);

  const selected = outlets.find(o => o.outlet_id === selectedId);

  // Sort: each primary outlet followed by its sub-outlets, then orphaned subs
  // (sub with no matching parent in this list) at the end. Stable for primaries.
  const sortedOutlets = useMemo(() => {
    const primaries = outlets.filter(o => o.hierarchy !== 'sub');
    const subs = outlets.filter(o => o.hierarchy === 'sub');
    const subsByParent = {};
    subs.forEach(s => {
      const k = s.parent_outlet_id || '__orphan__';
      (subsByParent[k] = subsByParent[k] || []).push(s);
    });
    const ordered = [];
    primaries.forEach(p => {
      ordered.push(p);
      (subsByParent[p.outlet_id] || []).forEach(s => ordered.push(s));
    });
    (subsByParent['__orphan__'] || []).forEach(s => ordered.push(s));
    return ordered;
  }, [outlets]);

  const handlePctChange = useCallback((outletId, key, val) => {
    const clamped = Math.max(0, Math.min(200, val));
    setPctMap(prev => {
      const next = { ...prev, [outletId]: { ...(prev[outletId] || {}), [key]: clamped } };
      // Debounce save
      if (pctTimerRef.current[outletId]) clearTimeout(pctTimerRef.current[outletId]);
      pctTimerRef.current[outletId] = setTimeout(() => {
        onSavePctUsed?.(outletId, next[outletId]);
      }, 600);
      return next;
    });
  }, [onSavePctUsed]);

  if (!outlets.length) {
    return <p className="text-center text-[color:var(--color-text-muted)] py-8">No outlets to display</p>;
  }

  return (
    <div className="flex gap-4 items-start" data-testid="pnl-desktop-split">
      {/* ── LEFT: Outlet selector ── */}
      <aside className="w-[240px] shrink-0 sticky top-[72px] self-start" data-testid="pnl-outlet-sidebar">
        <div className="border border-[#E2E8DF] rounded-md bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-[#E2E8DF] bg-[#F9F9F8]">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--color-text-muted)]">Outlets ({outlets.length})</p>
          </div>
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
            {sortedOutlets.map(o => {
              const active = o.outlet_id === selectedId;
              const sales = o.total_sales || 0;
              const np = o.net_profit || 0;
              const npPct = sales > 0 ? (np / sales * 100).toFixed(1) : null;
              const npColor = np >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]';
              const isSub = o.hierarchy === 'sub';
              const excluded = o.include_in_totals === false;
              return (
                <button key={o.outlet_id}
                  onClick={() => setSelectedId(o.outlet_id)}
                  className={`w-full text-left px-3 py-2 border-b border-[#E2E8DF]/50 transition-colors flex flex-col gap-0.5
                    ${active ? 'bg-[color:var(--color-primary)]/[0.06] border-l-[3px] border-l-[var(--color-primary)]' : 'hover:bg-[#F3F5F2] border-l-[3px] border-l-transparent'}
                    ${isSub ? 'pl-6' : ''}`}
                  data-testid={`pnl-outlet-row-${o.outlet_id}`}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {isSub && <span className="text-[#9CA3AF] text-[11px] -ml-3 shrink-0" aria-hidden>↳</span>}
                    <span className={`text-[13px] truncate ${active ? 'font-semibold text-[color:var(--color-primary)]' : 'font-medium text-[color:var(--color-text-primary)]'}`}>{o.outlet_name}</span>
                    {isSub && <span className="text-[8px] font-bold uppercase tracking-wider text-[color:var(--color-text-muted)] bg-[#F3F5F2] px-1 py-px rounded shrink-0" data-testid={`pnl-sub-badge-${o.outlet_id}`}>Sub</span>}
                    {excluded && <span className="text-[8px] font-bold uppercase tracking-wider text-[#D4A017] bg-[#D4A017]/10 px-1 py-px rounded shrink-0" title="Excluded from portfolio totals">Excl</span>}
                    {o.finalized && <CheckCircle2 className="w-3 h-3 text-[#2D6A4F] shrink-0" />}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="text-[color:var(--color-text-muted)] tabular-nums">{fmt(sales)}</span>
                    {npPct != null && (
                      <span className={`font-semibold tabular-nums ${npColor}`}>NP {npPct}%</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* ── RIGHT: Full-width P&L view ── */}
      <section className="flex-1 min-w-0" data-testid="pnl-detail-panel">
        {selected ? (
          <>
            {/* Detail header — outlet name + status + Finalize action */}
            <div className="flex items-center justify-between gap-3 pb-2 mb-2 border-b border-[#E2E8DF]">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <h2 className="text-[15px] font-semibold text-[color:var(--color-text-primary)] truncate">{selected.outlet_name}</h2>
                <StatusTag finalized={selected.finalized === true} />
                {selected.finalized && selected.finalized_at && (
                  <span className="text-[10px] text-[color:var(--color-text-muted)]">Finalized {selected.finalized_at.slice(0, 10)} by {selected.finalized_by}</span>
                )}
                {selected.month_locked && !selected.finalized && (
                  <span className="text-[10px] text-[#C45A41] font-medium flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Month Locked</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!readOnly && !selected.finalized && (
                  <Button size="sm" className="h-7 px-2.5 bg-[#2D6A4F] hover:bg-[color:var(--color-primary-hover)] text-white text-xs gap-1"
                    onClick={() => onFinalize?.(selected)} data-testid={`finalize-btn-${selected.outlet_id}`}>
                    <FileCheck className="w-3 h-3" /> Finalize
                  </Button>
                )}
                {!readOnly && selected.finalized && (
                  <Button variant="outline" size="sm" className="h-7 px-2.5 text-[#C45A41] border-[#C45A41]/30 text-xs gap-1"
                    onClick={() => onUnfinalize?.(selected.outlet_id)} data-testid={`unfinalize-btn-${selected.outlet_id}`}>
                    <Unlock className="w-3 h-3" /> Unfinalize
                  </Button>
                )}
              </div>
            </div>

            {/* Body — keyed so per-outlet state resets cleanly on switch */}
            <PnLStatementBody
              key={selected.outlet_id}
              o={selected}
              pnlData={pnlData}
              localPctUsed={pctMap[selected.outlet_id] || {}}
              onPctChange={(key, val) => handlePctChange(selected.outlet_id, key, val)}
            />
          </>
        ) : (
          <p className="text-center text-[color:var(--color-text-muted)] py-8">Select an outlet</p>
        )}
      </section>
    </div>
  );
}

/* ──────────────── OUTLET P&L CARD (mobile accordion) ──────────────── */
function OutletPnlCard({ o, pnlData, onFinalize, onUnfinalize, onToggleLock, readOnly, template, onSaveToggles, onManageLines, onSavePctUsed }) {
  const [expanded, setExpanded] = useState(false);
  const [localToggles, setLocalToggles] = useState(o.toggles || {});
  const [localPctUsed, setLocalPctUsed] = useState(o.pct_used || {});
  const pctTimerRef = useRef(null);
  const sys = o.system || {};
  const fin = o.final || null;
  const isFinalized = o.finalized === true;
  const ns = isFinalized ? (fin?.net_sales || 0) : (sys.net_sales || 0);
  const hasBudget = o.budget_mtd > 0;
  const customFields = o.custom_fixed_fields || [];
  const hasTemplate = template && template.is_active && template.items?.length > 0;

  // Sync pct_used from server when outlet data changes
  useEffect(() => { setLocalPctUsed(o.pct_used || {}); }, [o.pct_used]);

  const handlePctUsedChange = useCallback((outletId, key, val) => {
    const clamped = Math.max(0, Math.min(200, val));
    setLocalPctUsed(prev => ({ ...prev, [key]: clamped }));
    // Debounce save
    if (pctTimerRef.current) clearTimeout(pctTimerRef.current);
    pctTimerRef.current = setTimeout(() => {
      const updated = { ...localPctUsed, [key]: clamped };
      onSavePctUsed?.(outletId, updated);
    }, 600);
  }, [localPctUsed, onSavePctUsed]);

  const getToggle = (key) => localToggles[key] || 'APPLY';
  const toggleItem = (key) => {
    const newVal = getToggle(key) === 'APPLY' ? 'NA' : 'APPLY';
    const updated = { ...localToggles, [key]: newVal };
    setLocalToggles(updated);
    onSaveToggles?.(o.outlet_id, updated);
  };

  // Dynamic calculation based on template + toggles
  const dynCalc = useMemo(() => {
    if (!hasTemplate || isFinalized) return null;
    const items = template.items;
    const revenueItems = items.filter(i => i.category === 'Revenue');
    const variableItems = items.filter(i => i.category === 'Variable Costs');
    const fixedItems = items.filter(i => i.category === 'Fixed Costs');

    // Revenue breakdown keys are components of net_sales — skip them in sum
    const revenueBreakdowns = new Set(['food_sale', 'beverage_sale', 'other_sale']);
    let totalRevenue = 0;
    revenueItems.forEach(item => {
      if ((localToggles[item.key] || 'APPLY') === 'APPLY') {
        if (revenueBreakdowns.has(item.data_key)) return; // skip breakdowns
        totalRevenue += getItemValue(item, o, pnlData);
      }
    });

    let totalVariable = 0;
    variableItems.forEach(item => {
      if ((localToggles[item.key] || 'APPLY') === 'APPLY') {
        totalVariable += getItemValue(item, o, pnlData);
      }
    });

    let totalFixed = 0;
    fixedItems.forEach(item => {
      if ((localToggles[item.key] || 'APPLY') === 'APPLY') {
        totalFixed += getItemValue(item, o, pnlData);
      }
    });

    // Add custom fixed fields (always apply)
    customFields.forEach(cf => {
      totalFixed += Math.round((cf.value || 0) / (pnlData?.days_in_month || 30) * (pnlData?.elapsed_days || 0));
    });

    const gp = totalRevenue - totalVariable;
    const np = gp - totalFixed;
    const baseForPct = totalRevenue || ns || 1;

    return {
      totalRevenue, totalVariable, totalFixed,
      grossProfit: gp,
      netProfit: np,
      gpPct: totalRevenue > 0 ? (gp / totalRevenue * 100).toFixed(1) : '0.0',
      npPct: totalRevenue > 0 ? (np / totalRevenue * 100).toFixed(1) : '0.0',
    };
  }, [hasTemplate, isFinalized, template, localToggles, o, pnlData, ns, customFields]);

  return (
    <Card className={`border-[#E2E8DF] shadow-sm overflow-hidden ${isFinalized ? 'border-l-4 border-l-[#2D6A4F]' : ''} ${o.hierarchy === 'sub' ? 'ml-4 sm:ml-6' : ''}`}
      data-testid={`pnl-card-${o.outlet_id}`}>
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0 cursor-pointer select-none"
        onClick={() => setExpanded(p => !p)} data-testid={`pnl-toggle-${o.outlet_id}`}>
        <div className="flex items-center gap-2 min-w-0">
          {o.hierarchy === 'sub' && <span className="text-[#9CA3AF] text-[12px] shrink-0" aria-hidden>↳</span>}
          <CardTitle className="text-sm font-semibold text-[color:var(--color-text-primary)] truncate">{o.outlet_name}</CardTitle>
          {o.hierarchy === 'sub' && <span className="text-[8px] font-bold uppercase tracking-wider text-[color:var(--color-text-muted)] bg-[#F3F5F2] px-1 py-px rounded shrink-0" data-testid={`pnl-sub-badge-mobile-${o.outlet_id}`}>Sub</span>}
          {o.include_in_totals === false && <span className="text-[8px] font-bold uppercase tracking-wider text-[#D4A017] bg-[#D4A017]/10 px-1 py-px rounded shrink-0" title="Excluded from portfolio totals">Excl</span>}
          <StatusTag finalized={isFinalized} />
          {!expanded && (
            <span className="text-xs text-[color:var(--color-text-muted)] hidden sm:inline ml-1">
              Sales: {fmt(o.total_sales)} · NP: {fmt(dynCalc ? dynCalc.netProfit : o.net_profit)} ({dynCalc ? dynCalc.npPct : o.net_margin_pct}%)
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!readOnly && !isFinalized && expanded && (
            <>
              <Button size="sm" className="h-7 px-2 bg-[#2D6A4F] hover:bg-[color:var(--color-primary-hover)] text-white text-xs gap-0.5"
                onClick={e => { e.stopPropagation(); onFinalize(o); }} data-testid={`finalize-btn-${o.outlet_id}`}>
                <FileCheck className="w-3.5 h-3.5" /> Finalize
              </Button>
              <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${o.month_locked ? 'text-[#C45A41]' : 'text-[color:var(--color-text-muted)]'}`}
                onClick={e => { e.stopPropagation(); onToggleLock(o); }} data-testid={`lock-btn-${o.outlet_id}`}>
                {o.month_locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
              </Button>
            </>
          )}
          {!readOnly && isFinalized && expanded && (
            <Button variant="outline" size="sm" className="h-7 px-2 text-[#C45A41] border-[#C45A41]/30 text-xs gap-0.5"
              onClick={e => { e.stopPropagation(); onUnfinalize(o.outlet_id); }} data-testid={`unfinalize-btn-${o.outlet_id}`}>
              <Unlock className="w-3.5 h-3.5" /> Unfinalize
            </Button>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-[color:var(--color-text-muted)] ml-1" /> : <ChevronDown className="w-4 h-4 text-[color:var(--color-text-muted)] ml-1" />}
        </div>
      </CardHeader>
      {expanded && (
      <CardContent className="px-4 pb-4 pt-0">
        {isFinalized && o.finalized_at && (
          <div className="mb-2 px-2.5 py-1 bg-[#2D6A4F]/5 rounded text-[10px] text-[#2D6A4F] font-medium flex items-center gap-1"
            data-testid={`finalized-info-${o.outlet_id}`}>
            <CheckCircle2 className="w-2.5 h-2.5" />
            Finalized {o.finalized_at.slice(0, 10)} by {o.finalized_by}
            {o.finalized_notes && <span className="text-[color:var(--color-text-muted)] font-normal ml-1">— {o.finalized_notes}</span>}
          </div>
        )}
        {o.month_locked && !isFinalized && (
          <div className="mb-2 px-2.5 py-1 bg-[#C45A41]/10 rounded text-[10px] text-[#C45A41] font-medium flex items-center gap-1">
            <Lock className="w-2.5 h-2.5" /> Month Locked
          </div>
        )}

        <PnLStatementBody o={o} pnlData={pnlData} localPctUsed={localPctUsed}
          onPctChange={(key, val) => handlePctUsedChange(o.outlet_id, key, val)} />
      </CardContent>
      )}
    </Card>
  );
}
